import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import Joyride, { Step, CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { getInitialTour } from './tours/initialTour';
import { getChannelsTour } from './tours/channelsTour';
import TourTooltip from '@components/common/TourTooltip/TourTooltip';
import { useActiveFeatureFlags } from '@infrastructure/featureFlags';
import { useTranslation } from 'react-i18next';
import TourBeacon from '@components/common/TourBeacon/TourBeacon';

export type TourDefinition = {
  key: string;
  name: string;
  description: string;
  route?: string;
  steps: Step[];
};
type StoredTourInfo = {
  lastStep: number;
  status: 'completed' | 'skipped' | 'incomplete' | null;
};
type TourStateMap = Record<string, StoredTourInfo>;
interface TourContextType {
  tours: Record<string, TourDefinition>; 
  activeTour: string | null;
  isRunning: boolean;
  // step on activeTour
  stepIndex: number;
  // total steps on activeTour
  totalSteps: number;
  // if restart=true, empieza de 0
  startTour: ( tourName: string, restart?: boolean ) => void;
  // resume tour ( if restart=true, empieza de 0 sino desde el lastStep)
  resumeTour: ( tourName: string, restart?: boolean ) => void;
  closeTour: () => void;
  getTourStatus: ( tourName: string ) => 'completed' | 'skipped' | 'incomplete' | null;
}

const TourContext = createContext<TourContextType | undefined>( undefined );

/**
 * Where the "which tours has this person already seen" map lives.
 *
 * ⚠️ It is NOT account-scoped — one flat map for the whole browser origin. That is
 * fine while a browser belongs to one seller, and wrong the moment two accounts
 * share one: without an explicit wipe the second account starts with every tour
 * already marked as seen, and never gets onboarded.
 *
 * Exported (rather than left inline in the provider) so `useAuth`'s signout can
 * clear it. Hoisted to module scope for the same reason — it used to be redeclared
 * on every render inside the component.
 *
 * The `//TODO: esto reemplazar por backend luego` on the two call sites still
 * stands, and moving this to the user profile is what actually fixes the scoping;
 * clearing on signout is the containment until then.
 */
export const TOURS_STORAGE_KEY = 'teselly_tours_state';

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation( 'tour' );
  const activeFlags = useActiveFeatureFlags();
   //AGREGAR MAS TOURS ACA
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTours: Record<string, TourDefinition> = {
    initial: getInitialTour( activeFlags, t ),
    channels: getChannelsTour( activeFlags, t )
  };

  const loadStoredTourMap = (): TourStateMap => {
    try {
      const raw = localStorage.getItem( TOURS_STORAGE_KEY );
      if ( !raw ) return {};
      return JSON.parse( raw ) as TourStateMap;
    } catch {
      return {};
    }
  };
  const [ tourStateMap, setTourStateMap ] = useState<TourStateMap>(() => loadStoredTourMap());

  // esto reemplazar por backend luego //TODO:
  useEffect(() => {
    localStorage.setItem( TOURS_STORAGE_KEY, JSON.stringify( tourStateMap ));
  }, [tourStateMap]);

  const [ activeTour, setActiveTour ] = useState<string | null>( null );
  const [ stepIndex, setStepIndex ] = useState<number>( 0 );
  const [ isRunning, setIsRunning ] = useState<boolean>( false );

  // logic for ends/skips/back/next/etc
   const handleJoyrideCallback = useCallback(
    ( data: CallBackProps ) => {
      const { action, index, type, status } = data;
      if ( !activeTour ) return;

      if ( action === ACTIONS.CLOSE ) {
        setTourStateMap(( prev ) => ({
          ...prev,
          [activeTour]: { lastStep: index, status: 'incomplete' }
        }));
        setIsRunning( false );
        setActiveTour( null );
        return;
      }

      const lastIndex = allTours[activeTour].steps.length - 1;

      if ( type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND ) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;

        if ( index === lastIndex && action === ACTIONS.NEXT ) {
          setTourStateMap(( prev ) => ({
            ...prev,
            [activeTour]: { lastStep: index, status: 'completed' }
          }));
          setIsRunning( false );
          setActiveTour( null );
          setStepIndex( 0 );
          return;
        }

        const clamped = Math.max( 0, Math.min( nextIndex, lastIndex ));
        setStepIndex( clamped );
        setTourStateMap(( prev ) => ({
          ...prev,
          [activeTour]: { lastStep: clamped, status: 'incomplete' }
        }));
      }

      if ( status === STATUS.FINISHED ) {
        setTourStateMap(( prev ) => ({
          ...prev,
          [activeTour]: { lastStep: index, status: 'completed' }
        }));
        setIsRunning( false );
        setActiveTour( null );
        setStepIndex( 0 );
      }

      if ( status === STATUS.SKIPPED ) {
        setTourStateMap(( prev ) => ({
          ...prev,
          [activeTour]: { lastStep: index, status: 'skipped' }
        }));
        setIsRunning( false );
        setActiveTour( null );
      }
    },
    [ activeTour, allTours ]
  );

  // start or restart a tour
  const startTour = useCallback(
    ( tourName: string, restart = false ) => {
      if ( !allTours[tourName]) return;

      let initialStep = 0;
      if ( !restart ) {
        const info = tourStateMap[tourName];
        if (
          info?.status === 'incomplete' &&
          info.lastStep >= 0 &&
          info.lastStep < allTours[tourName].steps.length
        ) {
          initialStep = info.lastStep;
        }
      }

      setActiveTour( tourName );
      setStepIndex( initialStep );
      setIsRunning( true );

      if ( restart && tourStateMap[tourName]) {
        setTourStateMap(( prev ) => {
          const copy = { ...prev };
          delete copy[tourName];
          return copy;
        });
      }
    },
    [ allTours, tourStateMap ]
  );

  const resumeTour = useCallback(
    ( tourName: string, restart = false ) => startTour( tourName, restart ),
    [startTour]
  );

  const closeTour = useCallback(() => {
    setIsRunning( false );
    setActiveTour( null );
  }, []);

  const getTourStatus = useCallback(
    ( tourName: string ): 'completed' | 'skipped' | 'incomplete' | null=> {
      const info = tourStateMap[tourName];
      if ( !info ) return null;
      return info.status;
    },
    [tourStateMap]
  );

  const stepsForActiveTour: Step[] = activeTour ? allTours[activeTour].steps : [];

  return (
    <TourContext.Provider
      value={{
        tours: allTours,
        activeTour,
        isRunning,
        stepIndex,
        totalSteps: activeTour ? allTours[activeTour].steps.length : 0,
        startTour,
        resumeTour,
        closeTour,
        getTourStatus
      }}
    >
      <Joyride
        steps={stepsForActiveTour}
        run={isRunning}
        stepIndex={stepIndex}
        continuous={true}
        scrollToFirstStep={true}
        showSkipButton={true}
        styles={{
          options: {
            primaryColor: '#006272',
            zIndex: 10000
          },
          tooltipContainer: {
            maxWidth: 300
          }
        }}
        callback={handleJoyrideCallback}
        tooltipComponent={TourTooltip}
        beaconComponent={TourBeacon}
      />
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const ctx = useContext( TourContext );
  if ( !ctx ) throw new Error( 'useTour must be used within a TourProvider' );
  return ctx;
};